import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { BrainCircuit, Gavel } from 'lucide-react';
import { trainingApi } from '../services/api';
import { useAppStore } from '../store/useAppStore';
import TrainingTabs from '../components/training/TrainingTabs';

/**
 * Design decisions (EP-26 FE-15 ST-3): each one an approval request raised on the item it
 * decides, decided by the programme's approvers group (training:approve, four eyes) -- never a
 * tickbox in a document. Decisions taken before the module are recorded with their source.
 */
type D = Record<string, any>;

const input = 'mt-0.5 block w-full px-2 py-1 text-sm border rounded dark:bg-gray-700 dark:border-gray-600';

export default function TrainingDecisionsPage() {
  const { programId = '' } = useParams<{ programId: string }>();
  const open = useAppStore((s) => s.setDetailItemId);
  const [programme, setProgramme] = useState<D | null>(null);
  const [rows, setRows] = useState<D[] | null>(null);
  const [mode, setMode] = useState<'raise' | 'record'>('raise');
  const [f, setF] = useState<D>({ subject: '', question: '', options: '', proposed: '', why: '', decidedBy: '', decidedAt: '', source: '' });
  const [status, setStatus] = useState('');
  const load = useCallback(() => {
    trainingApi.programme(programId).then(setProgramme).catch(() => {});
    trainingApi.decisions(programId).then((r) => setRows(r.decisions)).catch((e) => setStatus(String(e?.message || e)));
  }, [programId]);
  useEffect(() => { load(); }, [load]);
  const opts = (f.options as string).split('\n').map((o) => o.trim()).filter(Boolean);
  const set = (k: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setF({ ...f, [k]: e.target.value });

  const submit = async () => {
    setStatus('');
    try {
      if (mode === 'raise') {
        const out = await trainingApi.raiseDecision(programId, { subject: f.subject.trim(), question: f.question, options: opts, proposed: f.proposed, why: f.why });
        setStatus(`Raised ${out.key}: the programme's approvers decide it in Approvals or Chat.`);
      } else {
        const out = await trainingApi.recordDecision(programId, {
          subject: f.subject.trim(), question: f.question, options: opts, chosen: f.proposed, why: f.why,
          decidedBy: (f.decidedBy as string).split(',').map((x) => x.trim()).filter(Boolean), decidedAt: f.decidedAt, source: f.source });
        setStatus(`Recorded ${out.key} as decided.`);
      }
      setF({ ...f, question: '', options: '', proposed: '', why: '', source: '' });
      load();
    } catch (e: any) {
      setStatus(e?.message || 'Not saved.');
    }
  };

  const canRecord = !!programme?.can?.approve;
  return (
    <div className="p-6 space-y-4">
      <h1 className="text-xl font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2"><BrainCircuit className="w-5 h-5" /> Design decisions</h1>
      <TrainingTabs programId={programId} />
      <p className="text-sm text-gray-600 dark:text-gray-400">
        A design decision is an approval request on the item it decides: the question, the options and the one proposed.
        The programme's approvers decide it (never the person who raised it); approving adopts the proposed option on the item.
      </p>

      <section className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex gap-3 text-sm mb-2">
          <button type="button" onClick={() => setMode('raise')} className={mode === 'raise' ? 'font-semibold text-accent' : 'text-gray-500'}>Raise a decision</button>
          {canRecord && <button type="button" onClick={() => setMode('record')} className={mode === 'record' ? 'font-semibold text-accent' : 'text-gray-500'}>Record a past decision</button>}
        </div>
        <div className="grid gap-2 md:grid-cols-2">
          <label className="text-xs text-gray-600 dark:text-gray-400">Item it decides (id, e.g. task:… or feature:…)<input className={input} value={f.subject} onChange={set('subject')} /></label>
          <label className="text-xs text-gray-600 dark:text-gray-400">Question<input className={input} value={f.question} onChange={set('question')} placeholder="e.g. Rows per atom" /></label>
          <label className="text-xs text-gray-600 dark:text-gray-400">Options (one per line)<textarea rows={3} className={input} value={f.options} onChange={set('options')} /></label>
          <div className="space-y-2">
            <label className="text-xs text-gray-600 dark:text-gray-400">{mode === 'raise' ? 'Proposed option' : 'Option chosen'}
              <select className={input} value={f.proposed} onChange={set('proposed')}>
                <option value="">choose…</option>{opts.map((o) => <option key={o}>{o}</option>)}
              </select></label>
            <label className="text-xs text-gray-600 dark:text-gray-400">Why<input className={input} value={f.why} onChange={set('why')} /></label>
          </div>
          {mode === 'record' && (<>
            <label className="text-xs text-gray-600 dark:text-gray-400">Decided by (emails, comma-separated)<input className={input} value={f.decidedBy} onChange={set('decidedBy')} /></label>
            <label className="text-xs text-gray-600 dark:text-gray-400">Decided on<input type="date" className={input} value={f.decidedAt} onChange={set('decidedAt')} /></label>
            <label className="text-xs text-gray-600 dark:text-gray-400 md:col-span-2">Source (where it was decided)<input className={input} value={f.source} onChange={set('source')} placeholder="e.g. DESIGN_REVIEW_2026-09-29.md group 5, tickbox" /></label>
          </>)}
        </div>
        <button type="button" onClick={submit} disabled={!f.subject || !f.question || opts.length < 2 || !f.proposed}
          className="mt-3 px-3 py-1.5 text-sm rounded bg-accent text-white disabled:opacity-50 flex items-center gap-1"><Gavel className="w-4 h-4" />
          {mode === 'raise' ? 'Raise for approval' : 'Record as decided'}</button>
        {status && <p className="mt-2 text-xs text-gray-700 dark:text-gray-300">{status}</p>}
      </section>

      <section className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <h2 className="text-sm font-semibold mb-2">Decisions</h2>
        {rows === null ? <p className="text-xs text-gray-500">Reading…</p> : rows.length === 0 ? <p className="text-xs text-gray-500">None yet.</p> : (
          <table className="w-full text-xs">
            <thead><tr className="text-left text-gray-500"><th>Decision</th><th>Options</th><th>Outcome</th><th>Item</th></tr></thead>
            <tbody>
              {rows.map((d) => (
                <tr key={d._id} className="border-t border-gray-100 dark:border-gray-700 align-top">
                  <td className="py-1 pr-2"><button type="button" onClick={() => open(d._id)} className="text-left hover:text-accent">
                    <span className="font-mono text-[10px] text-gray-400 mr-1">{d.key}</span>{d.question}</button></td>
                  <td className="pr-2">{(d.options || []).map((o: string) => <div key={o} className={o === d.proposed ? 'font-medium' : 'text-gray-500'}>{o}{o === d.proposed ? ' (proposed)' : ''}</div>)}</td>
                  <td className="pr-2">{d.decided
                    ? <span className="text-emerald-700 dark:text-emerald-400">{d.decided.option} — {(d.decided.approvedBy || []).join(', ')}, {String(d.decided.at).slice(0, 10)}{d.decided.source ? ` (${d.decided.source})` : ''}</span>
                    : <span className="text-amber-700">{d.status === 'denied' ? 'denied' : 'awaiting the approvers'}</span>}</td>
                  <td><button type="button" onClick={() => open(d.subject)} className="font-mono text-[10px] hover:text-accent">{d.subject}</button></td>
                </tr>))}
            </tbody>
          </table>)}
      </section>
    </div>
  );
}
